import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { Section } from "@/components/Section";
import { ProgramCard, ProgramDetails, ProgramIntro, ProgramRow } from "@/components/services/ProgramCard";
import { JsonLd } from "@/components/JsonLd";
import { FaqAccordion } from "@/components/FaqAccordion";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SITE } from "@/content/site";
import { faqSchema } from "@/lib/schema";
import { BN_LANG_META, bnKeywords } from "@/content/bn/keywords";
import { bnHreflangLinks } from "@/lib/seo";

export type BnServiceContent = {
  path: string;
  enPath?: string;
  crumb: string;
  title: string;
  desc: string;
  keywords: string[];
  eyebrow: string;
  h1: React.ReactNode;
  subtitle: string;
  takeaways: string[];
  programTitle: string;
  programDesc: string;
  cta: string;
  deliverables: string[];
  outcomes: string[];
  price: string;
  faqs: { q: string; a: string }[];
  serviceName: string;
  serviceType: string;
  related: { to: "/bn/services/seo" | "/bn/services/aeo" | "/bn/services/geo"; label: string }[];
};

export function bnServiceHead(c: BnServiceContent) {
  const url = `${SITE.url}${c.path}`;
  return {
    meta: [
      { title: `${c.title} | ফ্রিল্যান্সার তামাল` },
      { name: "description", content: c.desc },
      { name: "keywords", content: bnKeywords(...c.keywords) },
      ...BN_LANG_META,
      { property: "og:title", content: c.title },
      { property: "og:description", content: c.desc },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: c.title },
      { name: "twitter:description", content: c.desc },
    ],
    links: bnHreflangLinks(c.path, c.enPath),
  };
}

export function BnServiceDetail({ c }: { c: BnServiceContent }) {
  const crumbs = [
    { name: "হোম", path: "/bn" },
    { name: "সার্ভিস", path: "/bn/services" },
    { name: c.crumb, path: c.path },
  ];
  return (
    <>
      <Breadcrumbs items={crumbs} />
      <Section headingLevel={1} eyebrow={c.eyebrow} title={c.h1} subtitle={c.subtitle}>
        <div className="max-w-3xl">
          <KeyTakeaways items={c.takeaways} />
        </div>
      </Section>

      <ProgramCard reveal={false}>
        <ProgramIntro label="সার্ভিস" title={c.programTitle} tagline={c.programDesc} lang="bn">
          <p className="mt-4 text-sm font-medium text-primary">{c.price}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/bn/book" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium">
              {c.cta} <ArrowUpRight size={16} />
            </Link>
            <Link to="/bn/pricing" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary">
              প্যাকেজ ও দাম
            </Link>
          </div>
        </ProgramIntro>
        <ProgramDetails
          deliverables={c.deliverables}
          outcomes={c.outcomes}
          headings={{ deliverables: "ডেলিভারেবল", outcomes: "লক্ষ্য" }}
        >
          <ProgramRow heading="সাধারণ প্রশ্ন">
            <FaqAccordion items={c.faqs} idPrefix={`faq-bn-${c.crumb}`} withSchema={false} />
          </ProgramRow>
        </ProgramDetails>
      </ProgramCard>

      <Section title="অন্যান্য সার্ভিস">
        <div className="flex flex-wrap gap-3">
          {c.related.map((r) => (
            <Link key={r.to} to={r.to} className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary">
              {r.label} <ArrowUpRight size={16} />
            </Link>
          ))}
          <Link to="/bn/services" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary">
            সব সার্ভিস <ArrowUpRight size={16} />
          </Link>
          <Link to="/bn/blog" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary">
            বাংলা গাইড পড়ুন <ArrowUpRight size={16} />
          </Link>
        </div>
      </Section>

      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Service",
        name: c.serviceName,
        serviceType: c.serviceType,
        description: c.desc,
        inLanguage: "bn-BD",
        url: `${SITE.url}${c.path}`,
        areaServed: ["Bangladesh", "Worldwide"],
        provider: { "@type": "Person", "@id": `${SITE.url}/#person`, name: SITE.fullName, url: `${SITE.url}/bn/about` },
      }} />
      <JsonLd data={faqSchema(c.faqs)} />
    </>
  );
}
